import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { Marker, Source, Layer, type MapRef } from '@vis.gl/react-maplibre';
import MapView from 'components/Map/MapView';
import { applyNeutralBackdrop, setBasemap } from 'utils/maplibre';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';
import { useMapFlyTo } from 'hooks/useMapFlyTo';
import { utmToLatLon } from 'utils/utmToLatLon';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppSelector } from 'store/hook';
import { selectLatestTagData } from 'store/device.slice';
import { selectAssets } from 'store/asset.slice';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { images } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import {
  createBillboardStyle,
  assetMovementSpeed,
  type AssetStatusInfo,
} from 'views/FleetOverview/components/AssetMap/utils';
import { useAssetLiveStatus } from 'hooks/useAssetLiveStatus';
import StatusCodesAPI from 'api/status-codes.api';
import { type StatusCodeRecord } from 'views/Settings/StatusCodes/types';
import { type Asset } from 'types/asset.types';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import {
  locationRing,
  locationCenterPoint,
  zoneColorHex,
} from 'views/DispatchImporters/RoadsImporter/roadsImporter.helpers';
import { convexHull, pointInGeoRing } from 'utils/geohash';
import type { LocationRecord } from './LocationImporterView';

function buildTruckLabel(bumperNumber: string, ignitionOn: boolean): string {
  const canvas = document.createElement('canvas');
  canvas.width = 150;
  canvas.height = 34;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const dotColor = ignitionOn ? '#4ade80' : '#f87171';
  ctx.fillStyle = 'rgba(10,15,30,0.88)';
  ctx.strokeStyle = ignitionOn ? '#4ade80' : '#94a3b8';
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(canvas.width - r, 0);
  ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
  ctx.lineTo(canvas.width, canvas.height - r);
  ctx.quadraticCurveTo(
    canvas.width,
    canvas.height,
    canvas.width - r,
    canvas.height
  );
  ctx.lineTo(r, canvas.height);
  ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = dotColor;
  ctx.beginPath();
  ctx.arc(11, canvas.height / 2, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(bumperNumber, 20, canvas.height / 2);
  return canvas.toDataURL();
}

// Exact same call Fleet Overview's AssetMap makes for its truck icon, so
// an asset renders identically here.
function getAssetPng(asset: Asset): string {
  return images[getImageName(asset, 'Small')] ?? '';
}

// Same haul-road network layer/filter Fleet Summary's AssetMap draws for the
// 'car' customer (src/views/FleetOverview/components/AssetMap/index.tsx).
const CAR_ROAD_LAYER_IDS = new Set(['1003']);
const CAR_ROAD_GEOJSON_PATH = '/geojson/my-layer.geojson';

const filterCarRoadFeatures = (geoJson: any) => ({
  ...geoJson,
  features: (geoJson?.features ?? []).filter((feature: any) =>
    CAR_ROAD_LAYER_IDS.has(String(feature?.properties?.layer ?? ''))
  ),
});

// Cached across modal opens so the ~2.7MB road-network file is only
// fetched once per session, not every time this modal is opened.
let cachedCarRoadGeoJson: any | null = null;
let cachedCarRoadGeoJsonPromise: Promise<any> | null = null;

const loadCarRoadGeoJson = async () => {
  if (cachedCarRoadGeoJson) return cachedCarRoadGeoJson;
  cachedCarRoadGeoJsonPromise ??= (async () => {
    const response = await fetch(CAR_ROAD_GEOJSON_PATH);
    return await response.json();
  })();
  cachedCarRoadGeoJson = await cachedCarRoadGeoJsonPromise;
  return cachedCarRoadGeoJson;
};

// Ids for the car haul-road overlay's MapLibre source/layer — only a line
// layer is added (matching the old Cesium GeoJsonDataSource's `fill:
// TRANSPARENT`), so any polygon rings in the file only draw as outlines.
const CAR_ROADS_SOURCE_ID = 'location-modal-car-roads';
const CAR_ROADS_LAYER_ID = 'location-modal-car-roads-line';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

interface TruckDot {
  deviceId: string;
  lat: number;
  lon: number;
  bumper: string;
  ignitionOn: boolean;
  speed: number;
}

interface LocationMapModalProps {
  open: boolean;
  onClose: () => void;
  location: LocationRecord | null;
  // Live truck billboards overlaid on the map. Defaults to true so existing
  // callers (dispatch planning, location importer) keep their current view.
  showTrucks?: boolean;
  // Optional GeoJSON LineString (raw geometry or a Feature wrapping one)
  // drawn as a route/path alongside the `location` pin.
  routeGeojson?: { type: string; coordinates: unknown } | null;
  // Same static haul-road network overlay Fleet Summary's AssetMap shows for
  // the 'car' customer. Off by default so existing callers are unaffected.
  showRoadNetwork?: boolean;
  // All of the customer's imported locations (loading/dumping/etc zones),
  // drawn the same way Fleet Summary's AssetMap and the Roads Importer draw
  // them — colored zone + name label. Off by default.
  showAllLocations?: boolean;
  // When set alongside `showAllLocations`, only locations whose
  // `locationType` matches one of these values (case-insensitive) are
  // drawn. Accepts a single type or a list. Omit to draw every location
  // type.
  locationTypeFilter?: string | string[];
  // Multiple discrete points to pin at once (e.g. every row currently in a
  // table), each labeled. Used instead of `location` for a "view all" map.
  multiPoints?: Array<{ id: string; name: string; lat: number; lon: number }>;
  // Multiple GeoJSON LineString routes to draw at once, paired with
  // `multiPoints` for the same "view all" use case.
  multiRoutes?: Array<{ id: string; coordinates: Array<[number, number]> }>;
  // Hides the bottom E/N/Elev coordinate footer. Those fields only mean
  // anything for surveyed locations (Location Importer, Dispatch Plan V2) —
  // callers pinning ad-hoc GPS points (e.g. Trip Report) have no real
  // easting/northing/elevation, so the footer would just show "0.00".
  hideCoordinates?: boolean;
}

const LocationMapModalContent = ({
  open,
  onClose,
  location,
  showTrucks = true,
  routeGeojson = null,
  showRoadNetwork = false,
  showAllLocations = false,
  locationTypeFilter,
  multiPoints = [],
  multiRoutes = [],
  hideCoordinates = false,
}: LocationMapModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mapRef = useRef<MapRef | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<NominatimResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasCameraFlown, setHasCameraFlown] = useState(false);
  const [cameraArrived, setCameraArrived] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const latestTagData = useAppSelector(selectLatestTagData);
  const assets = useAppSelector(selectAssets);
  const { proj4String, localOriginEasting, localOriginNorthing, unitSystem } =
    useAppSelector((s) => s.persistedReducer.customer);
  const { customerCode } = useCustomerInfo();
  const carRoadsAddedRef = useRef(false);

  const locationImporterApi = useRef(new LocationImporterAPI()).current;
  const [allLocations, setAllLocations] = useState<ImportedLocation[]>([]);
  // Tracks whether the customer's locations (used to build the site
  // containment boundary) have finished loading — the camera fly-to and
  // point rendering both wait on this so an out-of-site point never
  // flashes on screen before it gets filtered out.
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  useEffect(() => {
    if (!showAllLocations || !open) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await locationImporterApi.getLocations();
        if (!cancelled) setAllLocations(data ?? []);
      } catch (err) {
        console.error('Failed to load locations for map overlay', err);
      } finally {
        if (!cancelled) setLocationsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAllLocations, open, locationImporterApi]);

  // Nothing to wait for when `showAllLocations` is off — the containment
  // filter doesn't apply, so treat locations as immediately "loaded".
  const locationsReady = !showAllLocations || locationsLoaded;

  const allowedLocationTypes = useMemo(() => {
    if (!locationTypeFilter) return null;
    const types = Array.isArray(locationTypeFilter)
      ? locationTypeFilter
      : [locationTypeFilter];
    return new Set(types.map((type) => type.toLowerCase()));
  }, [locationTypeFilter]);

  const locationZones = useMemo(() => {
    if (!showAllLocations) return [];
    return allLocations
      .filter(
        (loc) =>
          loc.active &&
          (!allowedLocationTypes ||
            (loc.locationType != null &&
              allowedLocationTypes.has(loc.locationType.toLowerCase())))
      )
      .map((loc) => {
        const center = locationCenterPoint(loc);
        if (!center) return null;
        const ring = locationRing(loc);
        return {
          id: loc.locationId,
          name: loc.name,
          colorHex: zoneColorHex(loc.locationType, '#94a3b8'),
          boundary:
            ring.length >= 3 ? ring.flatMap((p) => [p.lon, p.lat]) : null,
          lat: center.lat,
          lon: center.lon,
        };
      })
      .filter((zone): zone is NonNullable<typeof zone> => zone !== null);
  }, [showAllLocations, allLocations, allowedLocationTypes]);

  // A rough "customer site" boundary — the convex hull over every active
  // location's drawn zoneGeog vertices (falling back to its centroid when it
  // has no drawn zone), padded outward so haul roads/trip points that travel
  // just past a drawn zone aren't rejected. Used to drop multi-point/
  // multi-route pins that land wildly outside the actual site (e.g. a bad
  // coordinate conversion putting a trip point on another continent)
  // instead of plotting them.
  const siteContainmentRing = useMemo(() => {
    if (!showAllLocations) return [];
    const points = allLocations
      .filter((loc) => loc.active)
      .flatMap((loc) => {
        const ring = locationRing(loc);
        if (ring.length >= 3) return ring;
        const center = locationCenterPoint(loc);
        return center ? [center] : [];
      });
    if (points.length === 0) return [];
    const hull = convexHull(points);
    if (hull.length < 3) return [];
    const centerLat = hull.reduce((s, p) => s + p.lat, 0) / hull.length;
    const centerLon = hull.reduce((s, p) => s + p.lon, 0) / hull.length;
    const PAD_FACTOR = 1.5;
    return hull.map((p) => ({
      lat: centerLat + (p.lat - centerLat) * PAD_FACTOR,
      lon: centerLon + (p.lon - centerLon) * PAD_FACTOR,
    }));
  }, [showAllLocations, allLocations]);

  const isWithinSite = useCallback(
    (lat: number, lon: number) =>
      siteContainmentRing.length < 3 ||
      pointInGeoRing(lat, lon, siteContainmentRing),
    [siteContainmentRing]
  );

  // Status color + live status, same source Fleet Overview's AssetMap uses
  // for its truck tooltip, so a truck's tooltip looks the same everywhere.
  // Only needed when trucks are actually shown and the dialog is open —
  // otherwise this would open an SSE connection and fetch status codes for
  // tooltips that never render (e.g. Trip Report (Sensor), showTrucks=false).
  const statusCodesApi = useRef(new StatusCodesAPI()).current;
  const [statusCodes, setStatusCodes] = useState<StatusCodeRecord[]>([]);
  useEffect(() => {
    if (!showTrucks || !open) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await statusCodesApi.getAll();
        if (!cancelled) setStatusCodes(data);
      } catch (_) {
        // non-critical; status color/label just won't show on the tooltip
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showTrucks, open, statusCodesApi]);

  const colorByStatus = useMemo(() => {
    const map = new Map<string, string>();
    statusCodes.forEach((sc) => {
      const key = sc.status?.trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, sc.colorCode);
    });
    return map;
  }, [statusCodes]);

  const [liveStatusByDevice, setLiveStatusByDevice] = useState<
    Record<string, { status: string; reason: string; description: string }>
  >({});
  useAssetLiveStatus((device, update) => {
    setLiveStatusByDevice((prev) => ({ ...prev, [device]: update }));
  }, showTrucks && open);

  const statusInfoByDevice = useMemo(() => {
    const map: Record<string, AssetStatusInfo> = {};
    Object.entries(liveStatusByDevice).forEach(([deviceId, s]) => {
      const statusKey = s.status?.trim().toLowerCase();
      map[deviceId] = {
        color: statusKey ? colorByStatus.get(statusKey) : undefined,
        label: s.description?.trim() || s.reason?.trim() || s.status,
      };
    });
    return map;
  }, [liveStatusByDevice, colorByStatus]);

  const assetInfoMap = useMemo<
    Record<string, { bumper: string; raw: Asset }>
  >(() => {
    const map: Record<string, { bumper: string; raw: Asset }> = {};
    assets.forEach((a) => {
      if (a.device?.deviceId) {
        map[a.device.deviceId] = {
          bumper: a.bumperNumber ?? a.device.deviceId,
          raw: a,
        };
      }
    });
    return map;
  }, [assets]);

  const truckDots = useMemo<TruckDot[]>(() => {
    return Object.entries(latestTagData)
      .map(([deviceId, s]) => {
        const lat = Number(s.gps_latitude);
        const lon = Number(s.gps_longitude);
        if (!isFinite(lat) || !isFinite(lon) || lat === 0 || lon === 0)
          return null;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
        const info = assetInfoMap[deviceId];
        return {
          deviceId,
          lat,
          lon,
          bumper: info?.bumper ?? deviceId,
          ignitionOn: String(s.sb_ignition_status) === 'true',
          speed: assetMovementSpeed(s),
        };
      })
      .filter((d): d is TruckDot => d !== null);
  }, [latestTagData, assetInfoMap]);

  const mineSiteCenter = useMemo<{ lat: number; lon: number } | null>(() => {
    if (!showTrucks || truckDots.length === 0) return null;
    const avgLat = truckDots.reduce((s, d) => s + d.lat, 0) / truckDots.length;
    const avgLon = truckDots.reduce((s, d) => s + d.lon, 0) / truckDots.length;
    return { lat: avgLat, lon: avgLon };
  }, [showTrucks, truckDots]);

  const coords = (() => {
    if (!location) return null;
    const centLat = location.centroidLat;
    const centLon = location.centroidLon;
    if (
      centLat != null &&
      centLon != null &&
      centLat !== 0 &&
      centLon !== 0 &&
      centLat >= -90 &&
      centLat <= 90 &&
      centLon >= -180 &&
      centLon <= 180
    ) {
      return { latitude: centLat, longitude: centLon };
    }
    return utmToLatLon(
      location.easting,
      location.northing,
      proj4String,
      localOriginEasting,
      localOriginNorthing
    );
  })();

  // Drop pins/routes that land outside the customer's site — e.g. a bad
  // coordinate conversion putting a trip point on another continent.
  const filteredMultiPoints = useMemo(
    () => multiPoints.filter((p) => isWithinSite(p.lat, p.lon)),
    [multiPoints, isWithinSite]
  );
  const filteredMultiRoutes = useMemo(
    () =>
      multiRoutes
        .map((r) => ({
          ...r,
          coordinates: r.coordinates.filter(([lon, lat]) =>
            isWithinSite(lat, lon)
          ),
        }))
        .filter((r) => r.coordinates.length >= 2),
    [multiRoutes, isWithinSite]
  );

  // Bounding box over every multi-point/multi-route coordinate, used to frame
  // the camera around all of them at once (the "view all" case).
  const multiBounds = useMemo(() => {
    const points: Array<{ lat: number; lon: number }> = [];
    filteredMultiPoints.forEach((p) => points.push({ lat: p.lat, lon: p.lon }));
    filteredMultiRoutes.forEach((r) => {
      r.coordinates.forEach(([lon, lat]) => points.push({ lat, lon }));
    });
    const valid = points.filter(
      (p) =>
        !isNaN(p.lat) &&
        !isNaN(p.lon) &&
        p.lat !== 0 &&
        p.lon !== 0 &&
        p.lat >= -90 &&
        p.lat <= 90 &&
        p.lon >= -180 &&
        p.lon <= 180
    );
    if (valid.length === 0) return null;
    const lats = valid.map((p) => p.lat);
    const lons = valid.map((p) => p.lon);
    return {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons),
    };
  }, [filteredMultiPoints, filteredMultiRoutes]);

  const markerColor = theme.palette.primary.main;
  const markerOutline = theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a2e';

  // Accepts either a raw LineString geometry or a Feature wrapping one.
  const routeLineCoordinates = useMemo(() => {
    const geom =
      routeGeojson && (routeGeojson as any).type === 'Feature'
        ? (routeGeojson as any).geometry
        : routeGeojson;
    if (
      !geom ||
      geom.type !== 'LineString' ||
      !Array.isArray(geom.coordinates) ||
      geom.coordinates.length < 2
    ) {
      return null;
    }
    return geom.coordinates as Array<[number, number]>;
  }, [routeGeojson]);

  const truckEntities = useMemo(() => {
    if (!showTrucks) return [];
    return truckDots.map((dot) => {
      const asset = assetInfoMap[dot.deviceId]?.raw;
      // Same status-colored tooltip card Fleet Overview's AssetMap draws —
      // falls back to the plain bumper-only label when the asset roster
      // hasn't loaded yet for this device.
      const labelImage = asset
        ? createBillboardStyle(
            dot.bumper,
            dot.speed,
            asset,
            dot.ignitionOn,
            dot.ignitionOn,
            unitSystem,
            t,
            [],
            statusInfoByDevice[dot.deviceId],
            customerCode === 'car'
          )
        : buildTruckLabel(dot.bumper, dot.ignitionOn);
      const pngImage = asset ? getAssetPng(asset) : '';
      return (
        <Marker
          key={dot.deviceId}
          longitude={dot.lon}
          latitude={dot.lat}
          anchor="center"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={labelImage}
              sx={{ height: 17, mb: '2px' }}
            />
            <Box component="img" src={pngImage} sx={{ height: 28 }} />
          </Box>
        </Marker>
      );
    });
  }, [showTrucks, truckDots, assetInfoMap, statusInfoByDevice, unitSystem, t]);

  const { flyTo: flyToBase, fitBounds: fitBoundsBase } = useMapFlyTo(mapRef);

  const flyTo = useCallback(
    (lat: number, lon: number, zoom = 15) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      flyToBase(lat, lon, zoom, 600);
      void map.once('moveend', () => {
        setCameraArrived(true);
      });
    },
    [flyToBase]
  );

  const flyToBounds = useCallback(
    (bounds: {
      minLat: number;
      maxLat: number;
      minLon: number;
      maxLon: number;
    }) => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      const padLat = Math.max((bounds.maxLat - bounds.minLat) * 0.3, 0.003);
      const padLon = Math.max((bounds.maxLon - bounds.minLon) * 0.3, 0.003);
      // Padding is already baked into the degree-space rectangle below
      // (matching the old Cesium Rectangle.fromDegrees call), so fitBounds
      // itself is given zero extra pixel padding.
      fitBoundsBase(
        [
          { lat: bounds.minLat - padLat, lon: bounds.minLon - padLon },
          { lat: bounds.maxLat + padLat, lon: bounds.maxLon + padLon },
        ],
        0,
        600
      );
      void map.once('moveend', () => {
        setCameraArrived(true);
      });
    },
    [fitBoundsBase]
  );

  const applyImagery = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map?.isStyleLoaded()) return false;
    if (hasNoSatelliteCoverage(customerCode)) {
      applyNeutralBackdrop(map);
    } else {
      setBasemap(map, 'neutralRoad');
    }
    setMapReady(true);
    return true;
  }, [customerCode]);

  useEffect(() => {
    if (!open) {
      setMapReady(false);
      setHasCameraFlown(false);
      setCameraArrived(false);
      setLocationsLoaded(false);
      setSearchText('');
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const tryApply = () => {
      if (cancelled) return;
      if (applyImagery()) return;
      if (attempts >= 20) return;
      attempts += 1;
      timer = setTimeout(tryApply, 200);
    };
    tryApply();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, applyImagery]);

  useEffect(() => {
    // Wait for the site containment data before the first fly-to — flying
    // to (and revealing) an unfiltered bounds/point first, then correcting
    // once locations load, is exactly the flash-then-drop the map showed.
    if (!mapReady || hasCameraFlown || !locationsReady) return;
    setHasCameraFlown(true);
    if (multiBounds) {
      flyToBounds(multiBounds);
    } else if (coords) {
      // Zoom picked to look similarly close to the old Cesium altitude (2000m).
      flyTo(coords.latitude, coords.longitude, 16);
    } else if (mineSiteCenter) {
      // Zoom picked to look similarly close to the old Cesium altitude (8000m).
      flyTo(mineSiteCenter.lat, mineSiteCenter.lon, 13);
    }
  }, [
    mapReady,
    hasCameraFlown,
    locationsReady,
    multiBounds,
    coords,
    mineSiteCenter,
    flyTo,
    flyToBounds,
  ]);

  // Same haul-road network overlay Fleet Summary shows for the 'car'
  // customer — loaded lazily once the map is ready, cached across opens.
  useEffect(() => {
    if (!showRoadNetwork || !mapReady || customerCode !== 'car') return;
    const map = mapRef.current?.getMap();
    if (!map || carRoadsAddedRef.current) return;

    let cancelled = false;
    void (async () => {
      try {
        const geoJson = await loadCarRoadGeoJson();
        if (cancelled) return;
        const roadGeoJson = filterCarRoadFeatures(geoJson);
        if (cancelled || !map.isStyleLoaded() || carRoadsAddedRef.current)
          return;
        if (map.getLayer(CAR_ROADS_LAYER_ID))
          map.removeLayer(CAR_ROADS_LAYER_ID);
        if (map.getSource(CAR_ROADS_SOURCE_ID))
          map.removeSource(CAR_ROADS_SOURCE_ID);
        map.addSource(CAR_ROADS_SOURCE_ID, {
          type: 'geojson',
          data: roadGeoJson,
        });
        map.addLayer({
          id: CAR_ROADS_LAYER_ID,
          type: 'line',
          source: CAR_ROADS_SOURCE_ID,
          paint: {
            'line-color': '#F5A623',
            'line-width': 1.25,
            'line-opacity': 0.72,
          },
        });
        carRoadsAddedRef.current = true;
      } catch {
        // non-critical; map still works without the road overlay
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showRoadNetwork, mapReady, customerCode]);

  // Detach the road overlay when the modal closes so reopening it doesn't
  // accumulate duplicate sources/layers on the (persistent) MapLibre map.
  useEffect(() => {
    if (open) return;
    const map = mapRef.current?.getMap();
    if (map && carRoadsAddedRef.current) {
      try {
        if (map.getLayer(CAR_ROADS_LAYER_ID))
          map.removeLayer(CAR_ROADS_LAYER_ID);
        if (map.getSource(CAR_ROADS_SOURCE_ID))
          map.removeSource(CAR_ROADS_SOURCE_ID);
      } catch {
        // map already torn down
      }
    }
    carRoadsAddedRef.current = false;
  }, [open]);

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query
        )}&format=json&limit=5`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data: NominatimResult[] = await res.json();
      setSearchResults(data);
      setShowResults(true);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const onSearchChange = (value: string) => {
    setSearchText(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!value.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      void handleSearch(value);
    }, 500);
  };

  const onSelectResult = (r: NominatimResult) => {
    setShowResults(false);
    setSearchText(r.display_name.split(',')[0]);
    // Zoom picked to look similarly close to the old Cesium altitude (3000m).
    flyTo(parseFloat(r.lat), parseFloat(r.lon), 15);
  };

  const hasValidCoords = coords !== null;
  const showMap =
    hasValidCoords || mineSiteCenter !== null || multiBounds !== null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 3,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.4),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.3),
              flexShrink: 0,
            }}
          >
            <FmdGoodOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {location?.name ?? t('loc_importer_map_title')}
            </Typography>
            {location?.locationType && (
              <Typography variant="caption" color="text.secondary">
                {location.locationType}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Search bar */}
      <Box sx={{ px: 3, pt: 1.5, pb: 1, position: 'relative' }}>
        {showResults && searchResults.length > 0 && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 24,
              right: 24,
              zIndex: 1400,
              mt: 0.5,
              maxHeight: 200,
              overflow: 'auto',
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.5),
              borderRadius: 2,
            }}
          >
            <List dense disablePadding>
              {searchResults.map((r) => (
                <ListItemButton
                  key={r.place_id}
                  onClick={() => {
                    onSelectResult(r);
                  }}
                  sx={{ py: 0.75, px: 1.5 }}
                >
                  <ListItemText
                    primary={r.display_name.split(',')[0]}
                    secondary={r.display_name.split(',').slice(1, 3).join(',')}
                    primaryTypographyProps={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.72rem',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Map */}
      <DialogContent sx={{ p: 0, height: 420, position: 'relative' }}>
        {!showMap ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: 'background.default',
            }}
          >
            <FmdGoodOutlinedIcon
              sx={{ fontSize: 48, color: 'text.disabled' }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {t('loc_importer_map_invalid')}
            </Typography>
            {location && (
              <Typography variant="caption" color="text.disabled">
                E: {location.easting} · N: {location.northing}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {!mapReady && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default',
                  zIndex: 5,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}

            <Box sx={{ width: '100%', height: '100%' }}>
              <MapView ref={mapRef}>
                {/* Selected location pin — hidden until the camera has
                    arrived so it doesn't flash at the default view */}
                {coords && cameraArrived && (
                  <Marker
                    longitude={coords.longitude}
                    latitude={coords.latitude}
                  >
                    <Box
                      title={location?.name}
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: markerColor,
                        border: `2px solid ${markerOutline}`,
                      }}
                    />
                  </Marker>
                )}

                {/* Trip route (GeoJSON LineString) */}
                {routeLineCoordinates && (
                  <Source
                    id="location-modal-route"
                    type="geojson"
                    data={{
                      type: 'Feature',
                      properties: {},
                      geometry: {
                        type: 'LineString',
                        coordinates: routeLineCoordinates,
                      },
                    }}
                  >
                    <Layer
                      id="location-modal-route-line"
                      type="line"
                      paint={{ 'line-color': markerColor, 'line-width': 4 }}
                    />
                  </Source>
                )}

                {/* Multiple pins at once — e.g. every row currently in a
                    table, for a "view all" map. Points outside the
                    customer's site are already filtered out. */}
                {cameraArrived &&
                  filteredMultiPoints.map((p) => (
                    <React.Fragment key={`multi-point-${p.id}`}>
                      <Marker longitude={p.lon} latitude={p.lat}>
                        <Box
                          title={p.name}
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            backgroundColor: markerColor,
                            border: `2px solid ${markerOutline}`,
                          }}
                        />
                      </Marker>
                      <Marker
                        longitude={p.lon}
                        latitude={p.lat}
                        anchor="top"
                        offset={[0, 10]}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: markerColor,
                            textShadow:
                              '0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.name}
                        </Typography>
                      </Marker>
                    </React.Fragment>
                  ))}

                {/* Multiple routes at once, paired with multiPoints. */}
                {filteredMultiRoutes.length > 0 && (
                  <Source
                    id="location-modal-multi-routes"
                    type="geojson"
                    data={{
                      type: 'FeatureCollection',
                      features: filteredMultiRoutes.map((r) => ({
                        type: 'Feature',
                        properties: { id: r.id },
                        geometry: {
                          type: 'LineString',
                          coordinates: r.coordinates,
                        },
                      })),
                    }}
                  >
                    <Layer
                      id="location-modal-multi-routes-line"
                      type="line"
                      paint={{ 'line-color': markerColor, 'line-width': 4 }}
                    />
                  </Source>
                )}

                {/* All imported locations (loading/dumping/etc zones) — same
                    colored-zone-plus-label style Fleet Summary's AssetMap and
                    the Roads Importer use. */}
                {locationZones.map((zone) => {
                  const ring: number[][] = [];
                  if (zone.boundary) {
                    for (let i = 0; i < zone.boundary.length; i += 2) {
                      ring.push([zone.boundary[i], zone.boundary[i + 1]]);
                    }
                    if (ring.length > 0) {
                      const [firstLon, firstLat] = ring[0];
                      const [lastLon, lastLat] = ring[ring.length - 1];
                      if (firstLon !== lastLon || firstLat !== lastLat) {
                        ring.push([firstLon, firstLat]);
                      }
                    }
                  }
                  return (
                    <React.Fragment key={`loc-zone-${zone.id}`}>
                      {ring.length >= 4 ? (
                        <Source
                          id={`loc-zone-${zone.id}`}
                          type="geojson"
                          data={{
                            type: 'Feature',
                            properties: {},
                            geometry: { type: 'Polygon', coordinates: [ring] },
                          }}
                        >
                          <Layer
                            id={`loc-zone-fill-${zone.id}`}
                            type="fill"
                            paint={{
                              'fill-color': zone.colorHex,
                              'fill-opacity': 0.6,
                            }}
                          />
                          <Layer
                            id={`loc-zone-line-${zone.id}`}
                            type="line"
                            paint={{
                              'line-color': zone.colorHex,
                              'line-width': 2,
                            }}
                          />
                        </Source>
                      ) : (
                        <Marker longitude={zone.lon} latitude={zone.lat}>
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: '50%',
                              backgroundColor: zone.colorHex,
                              opacity: 0.6,
                              border: `2px solid ${zone.colorHex}`,
                            }}
                          />
                        </Marker>
                      )}
                      <Marker longitude={zone.lon} latitude={zone.lat}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: zone.colorHex,
                            textShadow:
                              '0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                          }}
                        >
                          {zone.name}
                        </Typography>
                      </Marker>
                    </React.Fragment>
                  );
                })}

                {/* Live trucks with PNG image + bumper label */}
                {truckEntities}
              </MapView>
            </Box>

            {/* Coord + legend footer */}
            {!hideCoordinates && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  px: 2,
                  py: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: (th) => alpha(th.palette.background.paper, 0.88),
                  backdropFilter: 'blur(6px)',
                  borderTop: '1px solid',
                  borderColor: (th) => alpha(th.palette.divider, 0.4),
                  zIndex: 4,
                }}
              >
                <Box sx={{ display: 'flex', gap: 2.5 }}>
                  <CoordPill label="E" value={location?.easting ?? 0} />
                  <CoordPill label="N" value={location?.northing ?? 0} />
                  <CoordPill
                    label="Elev"
                    value={location?.elevation ?? 0}
                    unit="m"
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.4),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('loc_importer_map_close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CoordPill = ({
  label,
  value,
  unit,
}: {
  label: string;
  value: number;
  unit?: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {label}:
    </Typography>
    <Typography variant="caption" color="text.primary" fontFamily="monospace">
      {value.toFixed(2)}
      {unit ? ` ${unit}` : ''}
    </Typography>
  </Box>
);

export const LocationMapModal = (props: LocationMapModalProps) => (
  <CustomMuiThemeProvider>
    <LocationMapModalContent {...props} />
  </CustomMuiThemeProvider>
);
